import * as Sentry from '@sentry/react';

/**
 * Convert Python-style dictionary (single quotes) to valid JSON (double quotes)
 * Handles mixed quote scenarios where outer keys use single quotes
 * and inner strings use double quotes
 */
export function convertPythonDictToJSON(pythonDict: string): string {
  try {
    // Handle mixed quote scenarios where outer keys use single quotes
    // and inner strings use double quotes

    let result = pythonDict;
    const protectedStrings: string[] = [];
    let stringIndex = 0;

    // Find all single-quoted strings that contain double quotes
    // Use a more robust approach that handles all edge cases:
    // - Strings that start/end with double quotes
    // - Strings with multiple double quotes
    // - Strings with escaped single quotes
    // - Complex nested quote patterns
    const findSingleQuotedStringsWithDoubleQuotes = (
      str: string,
    ): Array<{
      match: string;
      content: string;
      start: number;
      end: number;
    }> => {
      const results: Array<{
        match: string;
        content: string;
        start: number;
        end: number;
      }> = [];
      let i = 0;

      while (i < str.length) {
        if (str[i] === "'") {
          const start = i;
          i++; // Skip opening quote

          // Find the closing single quote, handling escaped quotes
          while (i < str.length) {
            if (str[i] === "'" && (i === 0 || str[i - 1] !== '\\')) {
              break;
            }
            i++;
          }

          if (i < str.length) {
            const end = i;
            const content = str.slice(start + 1, end);

            // Check if this string contains double quotes
            if (content.includes('"')) {
              results.push({
                match: str.slice(start, end + 1),
                content: content,
                start: start,
                end: end + 1,
              });
            }
          }
        }
        i++;
      }

      return results;
    };

    const stringMatches = findSingleQuotedStringsWithDoubleQuotes(result);

    // Process matches in reverse order to avoid index shifting issues
    for (let i = stringMatches.length - 1; i >= 0; i--) {
      const match = stringMatches[i];
      const placeholder = `__PROTECTED_STRING_${stringIndex}__`;
      // Escape the double quotes in the string value
      const escapedString = match.content.replace(/"/g, '\\"');
      protectedStrings.push(escapedString);
      result =
        result.slice(0, match.start) +
        `'${placeholder}'` +
        result.slice(match.end);
      stringIndex++;
    }

    // Now convert all single quotes to double quotes
    result = result.replace(/'/g, '"');

    // Restore the protected strings with escaped double quotes
    protectedStrings.forEach((str, index) => {
      const placeholder = `__PROTECTED_STRING_${index}__`;
      result = result.replace(placeholder, str);
    });

    return result;
  } catch (error) {
    console.error('Error converting Python dict to JSON:', error);
    Sentry.captureException(error, {
      extra: { context: 'convert-python-dict' },
    });
    return pythonDict; // Return original if conversion fails
  }
}
